import { Badge, Modal, Space, Tooltip } from "antd"
import { CSSProperties, useState } from "react";
import { EyeOutlined, FileOutlined, HistoryOutlined, MailOutlined, RocketOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../../store/AuthContext/AuthContext";
import { useFetch } from "../../../../../hooks/useFetch/useFetch";
import { ButtonLZ } from "../../../../components/design/Buttons/ButtonLZ";
import TableLZM from "../../../../components/design/table/TableLZM";
import { TableFilter } from "../../../../../common/utils/FilterUtils";
import { LocationCardCompacted } from "../../../../components/model/business/FieldsGroup";
import { customRequest } from "../../../../../common/request/customRequest";
import { EditLocationEmailsModal } from "../drawers/EditLocationEmailsModal";
import { EditLocationReportConfigModal } from "../drawers/EditLocationReportConfigModal";
import { ReportType } from "../../../../components/design/reports/ReportType";
import { ReportEmails } from "../../../../components/design/reports/ReportEmails";
import { ReportSendStatus } from "../../../../components/design/reports/ReportSendStatus";
import { ReportLogsDrawer } from "../drawers/ReportLogsDrawer";
import { PreviewReportModal } from "../drawers/PreviewReportModal";
import { EmptyStateBox } from "../../../../components/design/EmptyStateBox/EmptyStateBox";
import { ReportPeriodDisplay } from "../reportPeriodUtils";

const emptyReportsStyle = {
	background: '#ffffff',
	border: '1px dashed #facc15',
	color: '#94a3b8',
	width: '100%',
	display: 'flex',
	boxSizing: 'border-box' as const,
};

const filledReportsStyle = {
	width: '100%',
};

const configLinkStyle: CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 5,
	padding: 0,
	border: 'none',
	background: 'transparent',
	color: '#8b9aab',
	fontSize: 12,
	fontWeight: 400,
	cursor: 'pointer',
};

const reportActionLinkStyle: CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 4,
	padding: '0px 7px',
	height: 18,
	lineHeight: '16px',
	borderRadius: 9,
	border: '1px solid #e2e8f0',
	background: '#ffffff',
	color: '#0f172a',
	fontSize: 11,
	fontWeight: 500,
	cursor: 'pointer',
};

export const ReportsByLocation = (props: { refreshDefinitions: any }) => {
	
	const [reports, setReports] = useState(null);		
	const [updateData, setUpdateData] = useState(false);	
	const [locationEmailsModal, setLocationEmailsModal] = useState<any>(null);
	const [locationReportsModal, setLocationReportsModal] = useState<any>(null);
	const [showPreviewModal, setShowPreviewModal] = useState<any>(null);
	const [reportLogsDrawer, setReportLogsDrawer] = useState<any>(null);
	const { projectGroup } = useAuth();    

	useFetch(
        `${process.env.REACT_APP_API}/projects/${projectGroup.project}/reports/${projectGroup.group}/locations`,
        {},
        [updateData, props.refreshDefinitions], (data) => {
			setReports(data);			
		}
    );	

	const sendEmailWithReport = (emails: string[], locationId: string, reportType: string, schedule_type: string) => {
		Modal.confirm({
			title: '¿Confirmas el envío del informe por email?',
			okText: 'Confirmar',
			cancelText: 'Cancelar',
			onOk: async () => {
				await customRequest(
					`${process.env.REACT_APP_API}/projects/${projectGroup.project}/server-reports/${locationId}/type/${reportType}/mail`,
					{
						method: 'POST',
						data: { emails, schedule_type },
					}
				);
				setUpdateData(!updateData);
			},
		});
	}

	const generateReport = (locationId: string, reportType: string, schedule_type: string) => {
		Modal.confirm({
			title: '¿Confirmas la generación del informe?',
			okText: 'Confirmar',
			cancelText: 'Cancelar',
			onOk: async () => {
				await customRequest(
					`${process.env.REACT_APP_API}/projects/${projectGroup.project}/server-reports/${locationId}/type/${reportType}/preview?schedule_type=${schedule_type}`,
					{ method: 'GET' }
				);
				setUpdateData(!updateData);
			},
		});
	}    

	const COLUMNS = [       
        {
			title: 'Local',
			key: 'location',			
			width: 280,
			filterDropdown: TableFilter,
			filterIcon: () => {
			  return <SearchOutlined />;
			},
			onFilter: (value: any, record: any) => {
			  return record.location.title?.toLowerCase().includes(value?.toLowerCase()) 
			  || record.location?.administrativeArea?.toLowerCase().includes(value?.toLowerCase())
			  || record.location?.locality?.toLowerCase().includes(value?.toLowerCase());
			},
			render: (text: string, record: any) => (				
				<>
					<LocationCardCompacted location={record.location} />
				</>
			)			
		},
		{
			title: 'Destinatarios',         
			key: 'reportConfig',
			width: 200,
			onCell: () => ({ style: { verticalAlign: 'middle' } }),
			render: (record: any) => {
				const reportConfig = record.reportConfigWithScheduledReports?.reportConfig;
				const emails = reportConfig?.emails || [];
				const hasEmails = emails.length > 0;

				return (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
						{hasEmails ? (
							<ReportEmails emails={emails} />
						) : (
							<EmptyStateBox
								compact
								icon={<MailOutlined />}
								title="No hay emails"
								style={emptyReportsStyle}
							/>
						)}
						{reportConfig && (
							<button
								type='button'
								onClick={() => setLocationEmailsModal(reportConfig)}
								style={configLinkStyle}
							>
								<SettingOutlined style={{ fontSize: 12 }} />
								{hasEmails ? 'Gestionar Destinatarios' : 'Configurar destinatarios'}
							</button>
						)}
					</div>
				);
			}
		},
		{
			title: 'Informes',
			key: 'reports',
			render: (reports: string[], record: any) => {
				const reportConfig = record.reportConfigWithScheduledReports?.reportConfig;
				const scheduled = record.reportConfigWithScheduledReports?.scheduledReports || [];
				const hasReports = scheduled.length > 0;

				return (
					<div style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 4,
						width: '100%',
						borderLeft: '3px solid slategray',
						paddingLeft: 20,
						background: 'linear-gradient(90deg, rgba(148, 163, 184, 0.08) 0%, rgba(148, 163, 184, 0) 45%)',
						borderRadius: '0 8px 8px 0',
					}}>
						{hasReports ? (
							<Space direction="vertical" size="middle" style={filledReportsStyle}>
								{scheduled.map((template: any, index: number) => {
									const previousPeriodCovered = !!template.previousPeriodCovered;
									const previousPeriodSent = !!template.previousPeriodSent;
									const previousPeriodOpen = template.previousPeriodOpen;
									const previousPeriodClose = template.previousPeriodClose;
									const openReport = () => setShowPreviewModal({
										locationId: record.location.composeId,
										type: template.type,
										schedule_type: template.scheduled?.schedule_type,
										execute: !previousPeriodCovered,
										...(previousPeriodOpen && previousPeriodClose ? {
											openTime: previousPeriodOpen,
											closeTime: previousPeriodClose,
										} : {}),
									});

									return (
									<div 
										key={index} 
										style={{ 
											display: 'grid', 
											gridTemplateColumns: '260px minmax(200px, 230px) minmax(180px, 1fr) auto', 
											alignItems: 'center', 
											gap: '16px',
											color: 'slategrey' 
										}}
									>
										<ReportType
											reportType={template.type}
											scope={true}
										/>
										<div style={{
											display: 'flex',
											alignItems: 'center',
											gap: 12,
											padding: '4px 10px',
											borderRadius: 8,
											border: '1px solid #cbd5e1',
											background: 'transparent',
											lineHeight: 1.2,
										}}>
											<span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
												<span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
													Periodo
												</span>
												<span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
													{template.scheduled?.schedule_type === 'weekly' ? 'Semanal' : 'Mensual'}
												</span>
											</span>
											<span style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0', minHeight: 24 }} />
											<span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
												<span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
													Envío de emails
												</span>
												<span style={{
													display: 'inline-flex',
													alignItems: 'center',
													gap: 5,
													fontSize: 12,
													fontWeight: 600,
													color: template.scheduled?.enabled ? 'dodgerblue' : '#64748b',
												}}>
													<Badge status={template.scheduled?.enabled ? 'processing' : 'default'} />
													{template.scheduled?.enabled ? 'Automatizado' : 'Manual'}
												</span>
											</span>
										</div>
										<div style={{
											display: 'flex',
											flexDirection: 'column',
											gap: 0,
											alignItems: 'stretch',
											padding: '3px 8px',
											borderRadius: 6,
											border: '1px solid #cbd5e1',
											background: 'transparent',
											lineHeight: 1.1,
										}}>
											<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' }}>
												<ReportSendStatus
													compact
													generated={previousPeriodCovered}
													sent={previousPeriodSent}
													sentTo={template.previousPeriodSentTo}
													failedTo={template.previousPeriodFailedTo}
													total={record.reportConfigWithScheduledReports?.reportConfig?.emails?.length}
												/>
												{previousPeriodCovered && (
													<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
														<Tooltip title="Ver">
															<button type='button' onClick={openReport} style={reportActionLinkStyle} aria-label="Ver">
																<EyeOutlined />
															</button>
														</Tooltip>
														<Tooltip title={previousPeriodSent ? 'Ya enviado' : 'Enviar'}>
															<button
																type='button'
																disabled={previousPeriodSent}
																onClick={() => sendEmailWithReport(
																	record.reportConfigWithScheduledReports?.reportConfig?.emails,
																	record.location.composeId,
																	template.type,
																	template.scheduled?.schedule_type
																)}
																style={{
																	...reportActionLinkStyle,
																	...(previousPeriodSent ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
																}}
																aria-label="Enviar"
															>
																<MailOutlined />
															</button>
														</Tooltip>
													</span>
												)}
											</div>
											{previousPeriodOpen && previousPeriodClose && (
												<ReportPeriodDisplay
													openTime={previousPeriodOpen}
													closeTime={previousPeriodClose}
													scheduledType={template.scheduled?.schedule_type}
													compact
													rangeOnly
												/>
											)}
										</div>
										<div style={{
											display: 'flex',
											gap: 8,
											alignItems: 'center',
											justifySelf: 'end',
											paddingLeft: 8,
											borderLeft: '1px solid #e2e8f0',
										}}>
											{previousPeriodCovered ? (
												<Tooltip title="Periodo anterior ya generado">
													<span>
														<ButtonLZ
															size='small'
															type='default'
															icon={<RocketOutlined />}
															disabled
														>
															Generar
														</ButtonLZ>
													</span>
												</Tooltip>
											) : (
												<ButtonLZ
													size='small'
													type='default'
													icon={<RocketOutlined />}
													onClick={() => generateReport(
														record.location.composeId,
														template.type,
														template.scheduled?.schedule_type
													)}
												>
													Generar
												</ButtonLZ>
											)}
											<Tooltip title="Histórico">
												<ButtonLZ
													size='small'
													onClick={() => setReportLogsDrawer({ type: template.type, locationId: record.location.composeId})}
													type='default'
												>
													<HistoryOutlined />
												</ButtonLZ>
											</Tooltip>
										</div>
									</div>
									);
								})}                 
							</Space>
						) : (
							<EmptyStateBox
								compact
								icon={<FileOutlined />}
								title="No hay informes asignados"
								style={emptyReportsStyle}
							/>
						)}
						{reportConfig && (
							<button
								type='button'
								onClick={() => setLocationReportsModal({ reportConfig, scheduledReports: scheduled })}
								style={configLinkStyle}
							>
								<SettingOutlined style={{ fontSize: 12 }} />
								Asignar Informes
							</button>
						)}
					</div>
				);
			}
		}		
    ];

    return (
		<>                    
			{reports && 
				<TableLZM					
				rowKey='id'
				columns={COLUMNS}
				dataSource={reports}/>
			}						
			{locationEmailsModal && (
				<EditLocationEmailsModal
					locationReportConfig={locationEmailsModal}
					onFinish={() => { setLocationEmailsModal(null); setUpdateData(!updateData); }}
				/>
			)}
			{locationReportsModal && (
				<EditLocationReportConfigModal
					locationReportConfig={locationReportsModal.reportConfig}
					scheduledReportList={locationReportsModal.scheduledReports}
					onFinish={() => { setLocationReportsModal(null); setUpdateData(!updateData); }}
				/>
			)}
			{showPreviewModal && (
				<PreviewReportModal
					reportInfo={showPreviewModal}
					onFinish={() => {
						const shouldRefresh = !!showPreviewModal.execute;
						setShowPreviewModal(null);
						if (shouldRefresh) setUpdateData(!updateData);
					}}
				/>
			)}			
			{reportLogsDrawer && (
				<ReportLogsDrawer
					reportInfo={reportLogsDrawer}
					onFinish={(changed) => {
						setReportLogsDrawer(null);
						if (changed) setUpdateData(!updateData);
					}}
				/>
			)}
		</>
    )	
}
